import { useState, useRef, useEffect, memo } from 'react';
import { Bell, LogOut, ChevronDown, UserCircle } from 'lucide-react';
import cloudbyzLogo from '../../assets/cloudbyz-logo.png';
import { useAppStore } from '../../stores/appStore';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrator',
  submitter: 'Regulatory Affairs',
  viewer: 'Read-Only Viewer',
};

interface HeaderProps {
  pageTitle: string;
  onNotificationClick: () => void;
  onLogoClick: () => void;
  onLogout?: () => void;
  onViewProfile?: () => void;
}

export const Header = memo(function Header({ pageTitle, onNotificationClick, onLogoClick, onLogout, onViewProfile }: HeaderProps) {
  const user = useAppStore((s) => s.user);
  const unreadCount = useAppStore((s) => s.notifications.filter((n) => !n.is_read).length);
  const userName = user?.name;
  const userEmail = user?.email;
  const userRole = user?.role;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const initials = userName
    ? userName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  return (
    <header className="bg-card border-b border-border flex-shrink-0" style={{ boxShadow: 'var(--shadow-sm)' }}>
      <div className="flex items-center justify-between px-6 h-[68px]">

        {/* Left – Logo + page title */}
        <div className="flex items-center gap-4 min-w-0">
          <button
            onClick={onLogoClick}
            className="flex items-center hover:opacity-80 transition-opacity flex-shrink-0"
          >
            <img src={cloudbyzLogo} alt="CloudByz" className="h-8 w-auto object-contain" />
          </button>

          <div className="h-5 w-px bg-border flex-shrink-0" />

          <h1 className="text-sm font-semibold text-foreground truncate">{pageTitle}</h1>
        </div>

        {/* Right – actions */}
        <div className="flex items-center gap-2.5 flex-shrink-0">
          {/* Notification bell */}
          <button
            onClick={onNotificationClick}
            aria-label={unreadCount > 0 ? `Notifications — ${unreadCount} unread` : 'Notifications'}
            className="relative w-9 h-9 flex items-center justify-center rounded-xl text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <Bell className="w-4.5 h-4.5" />
            {unreadCount > 0 && (
              <span
                className="absolute top-1.5 right-1.5 w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white"
                style={{ background: 'var(--destructive)', boxShadow: '0 0 0 2px var(--card)' }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Profile dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(o => !o)}
              className="flex items-center gap-2 pl-2 pr-2 py-1 rounded-xl hover:bg-accent transition-colors"
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #0057D9 0%, #007BFF 100%)' }}
              >
                {initials}
              </div>
              {/* Only show username in trigger — no role subtitle */}
              <p className="hidden sm:block text-xs font-semibold text-foreground leading-tight max-w-[110px] truncate">
                {userName}
              </p>
              <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-60 bg-card border border-border rounded-xl overflow-hidden z-50 animate-scale-in"
                   style={{ boxShadow: 'var(--shadow-lg)' }}>

                {/* User info panel */}
                <div className="px-4 py-3 bg-muted/40 border-b border-border">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                         style={{ background: 'linear-gradient(135deg, #0057D9 0%, #007BFF 100%)' }}>
                      {initials}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{userName}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{userEmail}</p>
                      <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary mt-0.5">
                        {ROLE_LABELS[userRole ?? ''] ?? 'User'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="py-1">
                  <button
                    onClick={() => { setDropdownOpen(false); onViewProfile?.(); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors"
                  >
                    <UserCircle className="w-4 h-4 text-muted-foreground" />
                    View Profile
                  </button>
                  <div className="mx-4 border-t border-border my-1" />
                  <button
                    onClick={() => { setDropdownOpen(false); onLogout?.(); }}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
});
